/**
 * vet-visit-view.js
 */

import React from 'react';
import { 
  Grid, 
  Typography, 
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { format, parseISO } from 'date-fns';
import useRecordStore from 'contexts/record-store';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerContent from 'components/menu-drawer/drawer-content';
import EditButton from 'components/edit-button';
import { UserContext } from 'contexts/user/user-context';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import Icon from 'components/icon';
import IconSection from 'components/icon-section';
import FilesView from 'components/files/view';
import RecordFooter from 'components/record-footer';
import InfoRow from 'components/info-row';


const useStyles = makeStyles(theme => ({
  locationContainer: {
    maxWidth: 450,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: 'none'
    }
  },
  mapContainer: {
    display: 'flex', 
    marginBottom: theme.spacing(3),
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-4),
      marginLeft: theme.spacing(5),
    }
  },
}));


// use wrapper to remount component when ID changes
export default function VetVisitView(props) {
  const { vetVisitId } = useParams();

  return <VetVisitViewImplementation
    vetVisitId={vetVisitId}
    key={vetVisitId}
    {...props}
  />;
}


function VetVisitViewImplementation(props) {
  const {
    vetVisitId,
    drawerState,
    onClose,
    basePath
  } = props;
  const classes = useStyles();
  let history = useHistory();
  const { user } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const vetVisitRecord = recordStore.useRecord(Types.Record.VET_VISIT, vetVisitId).ornull;
  const hasRecord = Boolean(vetVisitRecord);


  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(!hasRecord));

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord]);


  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
      children={<Icon type='mobile-close-drawer' />}
    />
  );


  const getEditButton = (isMobile) => !user.role.isSupervisor ? (
    <EditButton 
      disabled={!user.role.status.isActive}
      data-testid='edit-button'
      iconOnly={isMobile}
      to={makeLocation(
        basePath,
        'vetVisit',
        'edit',
        { vetVisitId },
        history.location
      )}
    />
  ) : null;


  return (
    <>
      {vetVisitRecord &&
      <DrawerContentBuilder
        mobileAppBar={{
          title: 'Vet Visit',
          rightActions: <>{closeButton}{getEditButton(true)}</>,
          recordGroupType: Types.RecordGroup.VET_VISIT,
        }}
        desktopHeader={{
          title: vetVisitRecord.get().name,
          actions: <>{getEditButton(false)}</>
        }}
        stickyHeader
        children={<>
          <MobilePageTitle title={vetVisitRecord.get().name} />
          <DrawerContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} className={classes.locationContainer}>

                {/* main */}
                <IconSection iconName='summary'>
                  <InfoRow title="Name" text={vetVisitRecord.get().name} />
                  <InfoRow title="Dog" text={vetVisitRecord.get().dog.name} />
                </IconSection>

                {/* location */}
                <IconSection iconName='location'>
                  <Typography variant="body1">
                    {vetVisitRecord.get().location || 'No location provided'}
                  </Typography>
                </IconSection>

                {/* datetime */}
                <IconSection iconName='event'>
                  <Typography variant="body1">
                    {format(parseISO(vetVisitRecord.get().date), 'MMM dd, yyyy h:mm aaa')}
                  </Typography>
                </IconSection>

                {/* care types */}
                <IconSection iconName='vet-care-type'>
                  <Typography variant="body1">
                    {vetVisitRecord.get().careTypes.map(type => type.customOtherName || type.name).join(', ')}
                  </Typography>
                </IconSection>

                {/* vaccinations */}
                <IconSection iconName='vaccination'>
                  {vetVisitRecord.get().vaccinations.length
                  ? <>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>Vaccination Type</TableCell>
                            <TableCell>Next Vaccination</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vetVisitRecord.get().vaccinations.map(vaccination =>
                            <TableRow key={vaccination.vaccinationTypeId}>
                              <TableCell>{vaccination.name}</TableCell> 
                              <TableCell>{ format(parseISO(vaccination.dateNextVaccination), 'MMM dd, yyyy')}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                  </>
                  : <>No vaccinations</>}
                </IconSection>
             
              </Grid>
            </Grid>

            {/* notes */}
            {vetVisitRecord.get().notes &&
            <IconSection iconName='comment'>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {vetVisitRecord.get().notes}
              </Typography>
            </IconSection>}

            {/* files */}
            <FilesView files={vetVisitRecord.get().files} />

            <RecordFooter
              createdBy={vetVisitRecord.get().createdBy}
              dateCreated={vetVisitRecord.get()?.dateTimeCreated}
            />

          </DrawerContent>   
        </>}
      />}
    </>
  );
}